// Profil-Nachbau F-10 (Bennys Freigabe 14.10.2026, BAU-BENACHRICHTIGUNG.md 1.1, 1.4 und 5.1 bis 5.4): Wörter, Typen, Aufrufe und
// reine Regeln der Seite „Benachrichtigungen“ ohne React, damit frontend/tests sie ohne Browser prüfen. Hier stehen die Typen und
// Aufrufe aller Wege aus PF-602, auch für den Firmenstandard (firma, PF-705), die Glocke (PF-607) und die Plattform-Vorgabe
// (PF-608). Titel, Auslöser und Empfängerkreis kommen immer vom Server (backend/src/administration/notifications/katalog.ts);
// was wirksam ist, rechnet nur der Server. Der Produktname kommt vom Aufrufer (brand.displayName, nach MK-1 immer CLAiRIS).
import { adminApi } from '../../administration/api';
import type { ErklaerInhalt } from '../../components/Erklaerung';

export type Gruppe = 'sicherheit' | 'konto' | 'kontingent' | 'hilfe' | 'neuigkeiten' | 'betrieb';

/** Das Ereignis eines Eintrags im Posteingang; null bei Mitteilungen des Anbieters (Rundschreiben). link: Ziel in der App. */
export interface EintragEreignis { schluessel: string; gruppe: Gruppe; gruppenTitel: string; link: string | null }
/** Ein Eintrag im Posteingang. id ist die Zeile des Empfängers (POST /me/messages/:id/read, Anker nachricht-{id}). */
export interface PosteingangEintrag {
  id: string; messageId: string; userId: number; organizationId: string | null; readAt: string | null; createdAt: string;
  subject: string; body: string; category: 'PFLICHT' | 'HINWEIS'; ereignis: EintragEreignis | null;
}
/** GET /me/messages?page=&nur=ungelesen (BAU 5.1): je Seite 20 Einträge; unread zählt immer alle ungelesenen. */
export interface Posteingang { items: PosteingangEintrag[]; total: number; unread: number; page: number; pageSize: number }
/** GET /me/messages/zaehler */
export interface Zaehler { ungelesen: number }

/** Eine Zeile der eigenen Wahl (GET /me/notifications; Antwort von PUT /me/notifications/:schluessel). darueber: was ohne eigene
 *  Wahl gilt (Firmenstandard bzw. Vorgabe von CLAiRIS); eigene: die gespeicherte Wahl, null folgt darueber; wirksam: was gilt. */
export interface EigeneZeile { schluessel: string; titel: string; ausloeser: string; pflicht: boolean; darueber: boolean; eigene: boolean | null; wirksam: boolean }
export interface EigeneGruppe { schluessel: Gruppe; titel: string; pflicht: boolean; ereignisse: EigeneZeile[] }
/** ebene FIRMA: das Konto gehört zu einer Firma, deren Firmenstandard gilt; CLAIRIS: Einzel- oder Betreiberkonto. Nur die Zeilen,
 *  die das Konto bekommen kann. */
export interface EigeneWahl { ebene: 'FIRMA' | 'CLAIRIS'; firma: { name: string } | null; gruppen: EigeneGruppe[] }

/** Eine Zeile des Firmenstandards (GET und PUT /organizations/:id/notification-defaults, BAU 5.3). plattform: was ohne
 *  Firmenstandard gilt; firmenstandard null: die Firma folgt der Plattform; revision 0: noch nie gespeichert; mitEigenerWahl:
 *  Personen der Firma mit eigener Wahl. Ohne „Sicherheit“ und ohne „Für den Betrieb“. */
export interface FirmenstandardZeile {
  schluessel: string; gruppe: Gruppe; gruppenTitel: string; titel: string; ausloeser: string; empfaengerkreis: string;
  plattform: boolean; firmenstandard: boolean | null; wirksam: boolean; revision: number; mitEigenerWahl: number;
}
export interface Firmenstandard { firma: { id: string; name: string }; personen: number; ereignisse: FirmenstandardZeile[] }

/** Eine Zeile der Plattform-Vorgabe (GET und PUT /platform/notification-defaults, BAU 5.4). katalog: Vorgabe im Code; plattform
 *  null: folgt dem Katalog. Ohne Pflichtzeilen, mit „Für den Betrieb“. */
export interface PlattformZeile {
  schluessel: string; gruppe: Gruppe; gruppenTitel: string; titel: string; ausloeser: string; empfaengerkreis: string;
  katalog: boolean; plattform: boolean | null; wirksam: boolean; revision: number;
}
/** schreiben: das Konto hat das Recht platform.settings.write. */
export interface PlattformVorgabe { schreiben: boolean; ereignisse: PlattformZeile[] }

/** Versionskonflikt beim Firmenstandard und bei der Plattform-Vorgabe (409, StaleConflict bietet „Neu laden“). */
export const VERALTET = 'STALE_NOTIFICATION_DEFAULTS';

const teil = encodeURIComponent;
// Die Wege liegen unter /api/administration (adminApi): Anmeldung, Ursprungsprüfung, no-store und Ratenbegrenzung gelten.
export const ladePosteingang = (page: number, nurUngelesen = false, signal?: AbortSignal) =>
  adminApi.get<Posteingang>(`/me/messages?page=${page}${nurUngelesen ? '&nur=ungelesen' : ''}`, signal);
export const ladeZaehler = (signal?: AbortSignal) => adminApi.get<Zaehler>('/me/messages/zaehler', signal);
export const alsGelesen = (id: string) => adminApi.post<{ success: boolean }>(`/me/messages/${teil(id)}/read`, {});
export const ladeEigeneWahl = (signal?: AbortSignal) => adminApi.get<EigeneWahl>('/me/notifications', signal);
/** enabled null: zurück zum Firmenstandard bzw. zur Vorgabe. */
export const setzeEigeneWahl = (schluessel: string, enabled: boolean | null) =>
  adminApi.put<EigeneZeile>(`/me/notifications/${teil(schluessel)}`, { enabled });
export const ladeFirmenstandard = (organizationId: string, signal?: AbortSignal) =>
  adminApi.get<Firmenstandard>(`/organizations/${teil(organizationId)}/notification-defaults`, signal);
/** enabled null: die Firma folgt der Plattform-Vorgabe. expectedRevision: revision der geladenen Zeile. */
export const setzeFirmenstandard = (organizationId: string, schluessel: string, enabled: boolean | null, expectedRevision: number) =>
  adminApi.put<FirmenstandardZeile>(`/organizations/${teil(organizationId)}/notification-defaults/${teil(schluessel)}`, { enabled, expectedRevision });
export const ladePlattformVorgabe = (signal?: AbortSignal) => adminApi.get<PlattformVorgabe>('/platform/notification-defaults', signal);
/** enabled null: zurück zur Vorgabe im Katalog. expectedRevision: revision der geladenen Zeile. */
export const setzePlattformVorgabe = (schluessel: string, enabled: boolean | null, expectedRevision: number) =>
  adminApi.put<PlattformZeile>(`/platform/notification-defaults/${teil(schluessel)}`, { enabled, expectedRevision });

export const BENACHRICHTIGUNGEN_PFAD = '/einstellungen/benachrichtigungen';
export const POSTEINGANG_ANKER = 'posteingang';
export const EMAIL_ANKER = 'e-mail-je-ereignis';
/** Die Zeile „Hinweise vom Anbieter“ trägt die Kennung des früheren Schalters „Hinweise per E-Mail“; alte Adressen und die Suche
 *  bleiben gültig. */
export const HINWEISE_SCHLUESSEL = 'neuigkeiten.hinweise';
export const HINWEISE_ZEILE_ANKER = 'hinweise-per-email';
export const nachrichtAnker = (id: string) => `nachricht-${id}`;

export const BENACHRICHTIGUNGEN_TEXTE = {
  titel: 'Benachrichtigungen',
  aktualisieren: 'Aktualisieren',
  posteingang: 'Posteingang',
  posteingangBeschreibung: (produkt: string) => `Meldungen von ${produkt} und Mitteilungen des Anbieters.`,
  pflicht: 'Pflicht',
  hinweis: 'Hinweis',
  gelesen: 'Gelesen',
  ungelesen: 'Ungelesen',
  alsGelesen: 'Als gelesen markieren',
  ansehen: 'Ansehen',
  leer: 'Sie haben noch keine Benachrichtigungen.',
  email: 'E-Mail je Ereignis',
  emailUnterzeile: 'Zu welchen Ereignissen Sie zusätzlich eine E-Mail bekommen.',
  emailFirma: (firma: string) => `Bleibt eine Zeile beim Firmenstandard, gilt, was ${firma} einstellt.`,
  sicherheit: 'Wichtig für die Sicherheit Ihres Kontos. Diese E-Mails kommen immer.',
  immerAn: 'Immer an',
  immerAnLabel: (titel: string) => `${titel}: immer an`,
  firmenstandard: 'Firmenstandard',
  vorgabe: (produkt: string) => `Vorgabe von ${produkt}`,
  ihreWahl: 'Ihre Wahl',
  an: 'an',
  aus: 'aus',
  zurueckFirma: 'Auf Firmenstandard zurücksetzen',
  zurueckVorgabe: 'Auf Vorgabe zurücksetzen',
  gespeichert: 'Gespeichert.',
  speicherFehler: 'Die Einstellung konnte nicht gespeichert werden.',
  laden: 'Benachrichtigungen werden geladen …',
  ladeFehler: 'Die Benachrichtigungen konnten nicht geladen werden.',
  erneut: 'Erneut versuchen',
} as const;
const T = BENACHRICHTIGUNGEN_TEXTE;

/** Beschreibung im Seitenkopf je Zahl der ungelesenen Einträge. */
export function beschreibungFuer(ungelesen: number): string {
  if (ungelesen === 1) return '1 ungelesene Benachrichtigung.';
  return ungelesen > 0 ? `${ungelesen} ungelesene Benachrichtigungen.` : 'Keine ungelesenen Benachrichtigungen.';
}

/** F-24 (PF-TX-rahmen-5, SPEC-rahmen 3.6 F5): wie die E-Mails je Ereignis funktionieren; bis F-24 Einleitung des Abschnitts.
 *  Teil 3 je Ebene: Firmenstandard (Konto einer Firma) oder Vorgabe (Einzel- und Betreiberkonto, auch solange die Wahl lädt). */
export function emailFenster(produkt: string, ebene: EigeneWahl['ebene'] | null, firma: string | null): ErklaerInhalt {
  const dritter = ebene === 'FIRMA' && firma
    ? { titel: '3. Firmenstandard', text: `${T.emailFirma(firma)} „${T.zurueckFirma}“ nimmt Ihre eigene Wahl zurück.` }
    : { titel: '3. Vorgabe', text: `Ohne eigene Wahl gilt die ${T.vorgabe(produkt)}. „${T.zurueckVorgabe}“ nimmt Ihre eigene Wahl zurück.` };
  return {
    frage: 'Wie funktionieren die E-Mails?',
    teile: [
      { titel: `1. Immer in ${produkt}`, text: `Jedes Ereignis sehen Sie in ${produkt} im Posteingang und an der Glocke.` },
      { titel: '2. Zusätzlich per E-Mail', text: 'Mit dem Schalter je Zeile wählen Sie, ob Sie dazu auch eine E-Mail bekommen. Die Wahl gilt sofort.' },
      dritter,
      { titel: '4. Sicherheit', text: 'E-Mails zur Sicherheit Ihres Kontos kommen immer; dafür gibt es keinen Schalter.' },
    ],
  };
}

export type DritteZeile = {
  /** „Firmenstandard“ oder „Vorgabe von CLAiRIS“ */
  ebene: string;
  darueber: 'an' | 'aus';
  /** nur, wenn die eigene Wahl abweicht; gleicht sie dem Wert darüber, zeigt die Zeile nur ihn */
  eigene: 'an' | 'aus' | null;
  text: string;
};
const wort = (an: boolean) => (an ? T.an : T.aus);

/** Die dritte Zeile einer wählbaren Zeile (BAU 1.1): „Firmenstandard: an“, „Firmenstandard: an · Ihre Wahl: aus“; beim Einzel-
 *  und Betreiberkonto „Vorgabe von CLAiRIS: an“. Die Seite setzt den geltenden Wert halbfett. */
export function zeileDritte(zeile: Pick<EigeneZeile, 'darueber' | 'eigene'>, ebene: EigeneWahl['ebene'], produkt: string): DritteZeile {
  const name = ebene === 'FIRMA' ? T.firmenstandard : T.vorgabe(produkt);
  const eigene = zeile.eigene !== null && zeile.eigene !== zeile.darueber ? wort(zeile.eigene) : null;
  const darueber = wort(zeile.darueber);
  return { ebene: name, darueber, eigene, text: eigene ? `${name}: ${darueber} · ${T.ihreWahl}: ${eigene}` : `${name}: ${darueber}` };
}

/** Der Knopf, der die eigene Wahl löscht (enabled: null). */
export const zuruecksetzenText = (ebene: EigeneWahl['ebene']) => (ebene === 'FIRMA' ? T.zurueckFirma : T.zurueckVorgabe);

// Nur relative Ziele in der App: genau ein „/“ am Anfang, kein Schema, kein Leer- oder Steuerzeichen (wie melden.ts am Server).
const RELATIV = /^\/(?![/\\])[^\s\\\u0000-\u001f\u007f]*$/;
export const posteingangAdresse = (id: string) => `${BENACHRICHTIGUNGEN_PFAD}#${nachrichtAnker(id)}`;
/** Das eigene Ziel eines Ereignisses („Ansehen“ auf der Seite); null bei Rundschreiben und Ereignissen ohne Ziel. */
export const eigenesZiel = (eintrag: Pick<PosteingangEintrag, 'ereignis'>): string | null => {
  const link = eintrag.ereignis?.link;
  return link && RELATIV.test(link) ? link : null;
};
/** Wohin ein Klick auf den Eintrag führt (Glocke): ein Ereignis zu seinem Ziel, ein Rundschreiben zu seiner Zeile hier. */
export const zielVon = (eintrag: Pick<PosteingangEintrag, 'id' | 'ereignis'>) => eigenesZiel(eintrag) ?? posteingangAdresse(eintrag.id);

/** {Art} eines Eintrags: der Gruppentitel des Ereignisses, bei Rundschreiben „Pflicht“ oder „Hinweis“. */
export const artVon = (eintrag: Pick<PosteingangEintrag, 'category' | 'ereignis'>) =>
  eintrag.ereignis?.gruppenTitel ?? (eintrag.category === 'PFLICHT' ? T.pflicht : T.hinweis);

const DATUM = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric' });
const UHRZEIT = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
/** „14.10.2026, 18:05“ in Wiener Zeit. */
export function datumZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  return `${DATUM.format(zeitpunkt)}, ${UHRZEIT.format(zeitpunkt)}`;
}

/** Die graue Zeile unter dem Betreff: „{TT.MM.JJJJ, HH:MM} · {Art} · Gelesen|Ungelesen“. */
export const metaZeile = (eintrag: Pick<PosteingangEintrag, 'createdAt' | 'readAt' | 'category' | 'ereignis'>) =>
  `${datumZeit(eintrag.createdAt)} · ${artVon(eintrag)} · ${eintrag.readAt ? T.gelesen : T.ungelesen}`;
